import React from 'react';
import { motion } from 'framer-motion';
import { Mountain, ExternalLink, Users, MapPin, ArrowRight, Compass, Mic, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageTransition } from '../components/PageTransition';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
export function EndsAdventuresPage() {
  return <PageTransition data-id="element-215">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden min-h-[70vh] flex items-center" data-id="element-216">
        <div className="absolute inset-0" data-id="element-217">
          <video autoPlay muted loop playsInline className="w-full h-full object-cover" data-id="element-218">
            <source src="https://endsadventures.com/wp-content/uploads/2025/05/2026-DESKTOP-web_banner_-_1920_x_1080_hd-720p.mp4" type="video/mp4" data-id="element-219" />
          </video>
          <div className="absolute inset-0 bg-dark-900/60" data-id="element-220"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-221">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-222">
            <span className="text-gold-500 font-medium tracking-wider uppercase text-sm mb-4 block" data-id="element-223">
              Est. 2012
            </span>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold text-white mb-6 leading-tight" data-id="element-224">
              Ends <span className="text-gold-500" data-id="element-225">Adventures</span>
            </h1>
            <p className="text-2xl font-serif text-white/90 mb-4" data-id="element-226">
              Exercise Your Faith
            </p>
            <p className="text-xl text-white/80 max-w-2xl mx-auto mb-10 leading-relaxed" data-id="element-227">
              Christ-Centered Adventure Events. #PrayGoRepeat
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-228">
              <a href="https://endsadventures.com/" target="_blank" rel="noopener noreferrer" data-id="element-229">
                <Button size="lg" rightIcon={<ExternalLink className="w-5 h-5" data-id="element-231" />} data-id="element-230">
                  Choose Your Adventure
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-dark-700" data-id="element-232">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-233">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-234">
            <Mountain className="w-12 h-12 text-gold-500 mx-auto mb-6" data-id="element-235" />
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-6" data-id="element-236">
              Encounter God in the Wild
            </h2>
            <p className="text-lg text-white/80 leading-relaxed mb-8" data-id="element-237">
              Since 2012, Ends Adventures has been a ministry dedicated to using
              outdoor adventure as a catalyst to strengthen faith and build
              authentic community. We believe that stepping away from the noise
              of everyday life and into the beauty of creation provides a unique
              space to hear God's voice, face challenges with resilience, and
              forge deep, lasting relationships with fellow believers.
            </p>
            <div className="w-24 h-1 bg-gold-500 mx-auto rounded-full" data-id="element-238"></div>
          </motion.div>
        </div>
      </section>

      {/* Crew Photo Section */}
      <section className="py-24 bg-dark-800 border-t border-dark-600" data-id="element-239">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-240">
          <div className="text-center mb-10" data-id="element-241">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4" data-id="element-242">
              Meet the Crew
            </h2>
            <p className="text-white/80 max-w-2xl mx-auto" data-id="element-243">
              One family having fun, working hard. The heart and soul behind
              every adventure.
            </p>
          </div>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="rounded-2xl overflow-hidden border border-dark-500 shadow-2xl" data-id="element-244">
            <img src="https://cdn.magicpatterns.com/uploads/nVBgdWjoJsmw8EM2yFdSDU/EndsCrew_2024_WithErin1-scaled-e1743166037452.jpg" alt="Ends Adventures Crew — One Family Having Fun Working Hard" className="w-full h-auto object-cover" data-id="element-245" />
          </motion.div>
        </div>
      </section>

      {/* What They Offer Section */}
      <section className="py-24 bg-dark-800 border-t border-dark-600" data-id="element-246">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-247">
          <div className="text-center mb-16" data-id="element-248">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4" data-id="element-249">
              Adventure Awaits
            </h2>
            <p className="text-white/80 max-w-2xl mx-auto" data-id="element-250">
              Whether you're looking for a physical challenge or a spiritual
              retreat, there's an adventure designed to stretch your faith.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-251">
            {[{
            title: 'Hiking Expeditions',
            description: 'Multi-day treks through breathtaking landscapes that challenge the body and renew the spirit.',
            icon: <Mountain className="w-6 h-6 text-gold-500" data-id="element-252" />
          }, {
            title: 'Mission Trips',
            description: 'Purpose-driven travel combining outdoor adventure with serving communities in need.',
            icon: <Globe className="w-6 h-6 text-gold-500" data-id="element-253" />
          }, {
            title: 'Retreats',
            description: 'Immersive weekend getaways focused on spiritual rest, teaching, and fellowship.',
            icon: <Compass className="w-6 h-6 text-gold-500" data-id="element-254" />
          }, {
            title: 'Community Events',
            description: 'Local gatherings and day trips designed to build relationships and encourage one another.',
            icon: <Users className="w-6 h-6 text-gold-500" data-id="element-255" />
          }].map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} data-id="element-256">
                <Card className="p-6 h-full bg-dark-700 border-dark-600 hover:border-gold-500/50 transition-colors" data-id="element-257">
                  <div className="w-12 h-12 rounded-full bg-dark-800 border border-dark-500 flex items-center justify-center mb-4" data-id="element-258">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-serif font-bold text-white mb-3" data-id="element-259">
                    {item.title}
                  </h3>
                  <p className="text-white/80 text-sm leading-relaxed" data-id="element-260">
                    {item.description}
                  </p>
                </Card>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Connection to 3 Doves Section */}
      <section className="py-24 bg-dark-700 border-t border-dark-600" data-id="element-261">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-262">
          <Card className="p-8 md:p-12 bg-dark-800 border-gold-500/30 relative overflow-hidden" data-id="element-263">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gold-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-264"></div>

            <div className="relative z-10 flex flex-col md:flex-row items-center gap-8 text-center md:text-left" data-id="element-265">
              <div className="w-24 h-24 rounded-full bg-dark-700 border-2 border-gold-500 flex items-center justify-center flex-shrink-0" data-id="element-266">
                <Mic className="w-10 h-10 text-gold-500" data-id="element-267" />
              </div>

              <div data-id="element-268">
                <h3 className="text-2xl font-serif font-bold text-white mb-3" data-id="element-269">
                  Hear the Story on 3 Doves
                </h3>
                <p className="text-white/80 leading-relaxed mb-6" data-id="element-270">
                  Justin Hanneken, Executive Director of Ends of Earth
                  Adventures, joined Jason on the 3 Doves Podcast to share how
                  adventure and faith collide. Listen to his incredible journey
                  of stepping into the unknown where God does His best work.
                </p>
                <Link to="/podcasts" data-id="element-271">
                  <Button variant="outline" rightIcon={<ArrowRight className="w-4 h-4" data-id="element-273" />} data-id="element-272">
                    Listen to Episodes 3 & 4
                  </Button>
                </Link>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden bg-dark-800" data-id="element-274">
        <div className="absolute inset-0 bg-gold-600/5" data-id="element-275"></div>
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10" data-id="element-276">
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-white mb-6" data-id="element-277">
            Ready to Step Out?
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto" data-id="element-278">
            Discover upcoming events, read stories of impact, and find out how
            you can exercise your faith with Ends Adventures.
          </p>
          <a href="https://endsadventures.com/" target="_blank" rel="noopener noreferrer" className="inline-block" data-id="element-279">
            <Button size="lg" rightIcon={<ExternalLink className="w-5 h-5" data-id="element-281" />} data-id="element-280">
              Visit Ends Adventures
            </Button>
          </a>
        </div>
      </section>
    </PageTransition>;
}